import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input, SingleDate, Tooltip } from '@node-space/ui-components'
import { FormFieldsRow } from 'components/Form/FormFieldsRow'
import { CountryCodes } from 'types/types'
import { INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA } from '../helpers/form'
import { ConfirmIndividualCustomerMethod, IndividualCustomerFormData } from '../helpers/types'
import { useIndividualCustomerForm } from '../hooks/useIndividualCustomerForm'
import { AddCustomerActions } from './AddCustomerActions'

export interface AddIndividualCustomerProps {
  defaultValues: IndividualCustomerFormData
  onConfirm: ConfirmIndividualCustomerMethod
  onCancel: () => void
}

export const AddIndividualCustomer = ({
  defaultValues,
  onConfirm,
  onCancel,
}: AddIndividualCustomerProps) => {
  const { t } = useTranslation()
  const { formFields, formValues, displayData, hasErrors, handleSubmit } =
    useIndividualCustomerForm(defaultValues ?? INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA)

  const onSubmit = (data: IndividualCustomerFormData) => {
    onConfirm({ data, displayData })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box flexCol gapY={16}>
        <FormFieldsRow
          leftField={<Input {...formFields?.firstName} />}
          rightField={<Input {...formFields?.lastName} />}
        />
        <SingleDate {...formFields?.dateOfBirth} />
        <Combobox {...formFields?.nationality} />
        <Input {...formFields?.emailAddress} />
        <h3 className="text-heading-3 text-prominent pt-2">{t('customers.residentialAddress')}</h3>
        <Input {...formFields?.addressLine1} />
        <Input {...formFields?.addressLine2} optionalText={t('optional')} />
        <FormFieldsRow
          leftField={<Input {...formFields?.city} />}
          rightField={<Input {...formFields?.postalCode} />}
        />
        <Combobox {...formFields?.countryCode} />
        {formValues?.countryCode === CountryCodes.US && <Combobox {...formFields?.stateCode} />}
        <h3 className="text-heading-3 text-prominent pt-2">
          {t('customers.complianceInformation')}
        </h3>

        <FormFieldsRow
          leftField={
            <Input
              {...formFields?.taxIdentificationNumber}
              placeholder={t('customers.taxIdentificationPlaceholder')}
              Tooltip={
                <Tooltip type="complex" bodyContent={t('customers.taxIdentificationTooltip')} />
              }
            />
          }
          rightField={
            <Box className="pt-5">
              <Combobox {...formFields?.taxResidenceCountryCode} label="" />
            </Box>
          }
        />
        <Combobox
          {...formFields?.employmentStatus}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.employmentStatusTooltip')} />}
        />
        <Combobox
          {...formFields?.sourceOfFunds}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.sourceOfFundsTooltip')} />}
        />
        <Combobox
          {...formFields?.pepStatus}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.pepStatusTooltip')} />}
        />
        <Combobox
          {...formFields?.riskScore}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.riskScoreTooltip')} />}
        />
        <Box flex gapX={12}>
          <Box flexSize="fill">
            <Input
              {...formFields?.expectedMonthlyVolumeAmount}
              Tooltip={
                <Tooltip type="complex" bodyContent={t('customers.monthlyVolumesTooltip')} />
              }
            />
          </Box>
          <Box className="pt-5 w-[148px]">
            <Combobox {...formFields?.expectedMonthlyVolumeCurrency} label="" />
          </Box>
        </Box>
        <Combobox
          {...formFields?.useCase}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.serviceTypeTooltip')} />}
        />
        <Combobox
          {...formFields?.description}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.intendedUseTooltip')} />}
        />
        <Input
          {...formFields?.externalReference}
          testId="individual-external-reference"
          optionalText={t('optional')}
          Tooltip={<Tooltip type="complex" bodyContent={t('customers.externalReferenceTooltip')} />}
        />
      </Box>

      <AddCustomerActions onCancel={onCancel} isDisableContinue={hasErrors} />
    </form>
  )
}
